import { type SessionFlowFailure, TrackingKey } from '@heidi/runtime/performance';

import {
  classifySaveRequestFailure,
  namedError,
  openSaveRequestAttempt,
  type SaveRequestAttempt,
  type SaveRequestFailure,
} from './saveRequestAttempt';

// `document_update` client metrics: one attempt per autosave request, after the
// debounce has coalesced the edits. Autosave is autonomous, so no intent.
// A queued save the editor drops before sending is not an attempt.
// A newer save never aborts an older one, so each closes with its own outcome.
// The attempt opens at dispatch to count offline waits; logout settles it as cancelled.
// A page exit without settlement leaves it for the abandonment snapshot.

/** The editor whose autosave sent the request. Diagnostic only: RUM context. */
export type DocumentAutosaveEditor =
  | 'markdown_native'
  | 'block_editor_html'
  | 'block_editor_markdown';

export type DocumentUpdateAttempt = { editor: DocumentAutosaveEditor };

export type DocumentUpdateErrorName =
  | 'DocumentUpdateNetworkUnavailable'
  | 'DocumentUpdateTimeout'
  | 'DocumentUpdateServerError'
  | 'DocumentUpdateRejected'
  | 'DocumentUpdateForbidden'
  | 'DocumentUpdateInterrupted'
  | 'DocumentUpdateUnclassified';

/**
 * The `document_update` failure vocabulary, registered with the rest of the
 * session-flow names. `DocumentUpdateUnclassified` is absent on purpose, so it
 * lands in `_OTHER`.
 */
export const DOCUMENT_UPDATE_FAILURE_TYPES: Readonly<
  Record<
    Exclude<DocumentUpdateErrorName, 'DocumentUpdateUnclassified'>,
    SessionFlowFailure
  >
> = {
  DocumentUpdateNetworkUnavailable: {
    outcome: 'failed',
    errorType: 'network_unavailable',
  },
  DocumentUpdateTimeout: { outcome: 'failed', errorType: 'request_timeout' },
  DocumentUpdateServerError: { outcome: 'failed', errorType: 'server_error' },
  DocumentUpdateRejected: { outcome: 'failed', errorType: 'request_rejected' },
  // Lost access to the session: a precondition, not a save fault.
  DocumentUpdateForbidden: { outcome: 'blocked', errorType: 'permission_denied' },
  // Session expiry or logout ended the save, not the save itself.
  DocumentUpdateInterrupted: { outcome: 'cancelled' },
};

const DOCUMENT_UPDATE_ERROR_BY_FAILURE: Readonly<
  Record<SaveRequestFailure, DocumentUpdateErrorName>
> = {
  network_unavailable: 'DocumentUpdateNetworkUnavailable',
  timeout: 'DocumentUpdateTimeout',
  server_error: 'DocumentUpdateServerError',
  rejected: 'DocumentUpdateRejected',
  forbidden: 'DocumentUpdateForbidden',
  interrupted: 'DocumentUpdateInterrupted',
  unclassified: 'DocumentUpdateUnclassified',
};

const documentUpdateError = (failure: SaveRequestFailure): Error =>
  namedError(DOCUMENT_UPDATE_ERROR_BY_FAILURE[failure]);

/**
 * Maps a save rejection to a bounded name; an unknown cause lands in `_OTHER`.
 * The raw value is never forwarded: its name may mean something else for
 * another operation in the shared name map.
 */
export const classifyDocumentUpdateError = (error: unknown): Error =>
  documentUpdateError(classifySaveRequestFailure(error));

/**
 * Opens one autosave request as a `document_update` attempt, at dispatch. The
 * caller settles it with the request's result or error.
 */
export const openDocumentUpdateAttempt = (
  attempt: DocumentUpdateAttempt
): SaveRequestAttempt =>
  openSaveRequestAttempt({
    key: TrackingKey.SCRIBE_DOCUMENT_UPDATE,
    editor: attempt.editor,
    errorFor: documentUpdateError,
    scope: 'documentUpdateTracking',
    tagOfflineAtDispatch: true,
  });
